import React, { useEffect } from "react";
import {
  ArrowRightIcon,
  BanknotesIcon,
  BoltIcon,
  CheckCircleIcon,
  CircleStackIcon,
  CodeBracketIcon,
  CommandLineIcon,
  CpuChipIcon,
  CubeTransparentIcon,
  EnvelopeIcon,
  FingerPrintIcon,
  IdentificationIcon,
  KeyIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  WalletIcon,
} from "@heroicons/react/24/outline";
import TechnologyCanvas from "../components/TechnologyCanvas";
import Web3Contact from "../components/Web3Contact";

const metrics = [
  { value: "04", label: "Core domains" },
  { value: "EVM", label: "Compatible network" },
  { value: "ETH · USDT", label: "Asset rails" },
  { value: "End-to-End", label: "Delivery scope" },
];

const capabilities = [
  {
    index: "01",
    icon: CubeTransparentIcon,
    accent: "text-emerald-400 border-emerald-400",
    title: "Smart Contracts",
    subtitle: "Smart contract engineering",
    summary:
      "Design composable, upgrade-aware onchain boundaries so asset rules remain public, deterministic, and verifiable.",
    items: [
      "Solidity 0.8.20",
      "ERC-721A / Token Standards",
      "OpenZeppelin Security",
      "Modular Architecture",
    ],
  },
  {
    index: "02",
    icon: WalletIcon,
    accent: "text-cyan-400 border-cyan-400",
    title: "Wallet Systems",
    subtitle: "Wallet and identity systems",
    summary:
      "Turn signing, network management, and asset state into a clear and trustworthy product experience.",
    items: [
      "MetaMask / EIP-1193",
      "Ethers.js v6",
      "Message & Transaction Signing",
      "Account / Network Events",
    ],
  },
  {
    index: "03",
    icon: CpuChipIcon,
    accent: "text-lime-400 border-lime-400",
    title: "Onchain Algorithms",
    subtitle: "Onchain algorithms and rule engines",
    summary:
      "Build deterministic computation around precision, state transitions, and gas efficiency.",
    items: [
      "State Machines",
      "Hash & Signature Validation",
      "Batch Computation",
      "Gas Optimization",
    ],
  },
  {
    index: "04",
    icon: ServerStackIcon,
    accent: "text-amber-400 border-amber-400",
    title: "Gateway Infrastructure",
    subtitle: "Gateway and data infrastructure",
    summary:
      "Connect clients, RPC endpoints, contract events, and business services through resilient data paths.",
    items: [
      "RPC Gateway",
      "Event Synchronization",
      "API Adapters",
      "Retry & Observability",
    ],
  },
];

const useCases = [
  {
    icon: CubeTransparentIcon,
    title: "Brand Digital Assets",
    detail: "Collectibles, limited editions, and IP-linked assets",
  },
  {
    icon: IdentificationIcon,
    title: "Membership & Loyalty",
    detail: "Membership credentials, tiered access, and communities",
  },
  {
    icon: FingerPrintIcon,
    title: "Digital Credentials & Provenance",
    detail: "Certificates, product identity, and lifecycle records",
  },
  {
    icon: BanknotesIcon,
    title: "Onchain Payments & Settlement",
    detail: "Native assets, stablecoins, and automated routing",
  },
  {
    icon: CircleStackIcon,
    title: "Asset Marketplaces",
    detail: "Listing, purchasing, cancellation, and trade history",
  },
  {
    icon: CommandLineIcon,
    title: "Web3 Asset Console",
    detail: "Asset visibility, access control, and operations",
  },
];

const architecture = [
  { name: "Client", detail: "Web / Mobile" },
  { name: "Gateway", detail: "RPC · API" },
  { name: "Wallet SDK", detail: "Identity · Sign" },
  { name: "Algorithms", detail: "Rules · Compute" },
  { name: "Contracts", detail: "Assets · State" },
  { name: "EVM", detail: "Network · Finality" },
];

const engineeringControls = [
  "Contract access control, pausing, and reentrancy protection",
  "SafeERC20 asset operations",
  "Unit tests and critical-path regression",
  "Gas reporting and coverage analysis",
  "Testnet-to-mainnet environment isolation",
  "Onchain event and failure-state monitoring",
];

const stack = [
  "Solidity",
  "ERC-721A",
  "OpenZeppelin",
  "Hardhat",
  "Ethers.js v6",
  "TypeChain",
  "React 18",
  "MetaMask",
  "JSON-RPC",
  "USDT",
  "Tailwind CSS",
];

const PageHeader = () => (
  <nav className="sticky top-0 z-50 border-b border-white/10 bg-black/80 backdrop-blur-xl">
    <div className="container-v3 flex h-16 items-center justify-between">
      <a href="#top" className="font-mono text-sm font-bold text-white">
        Web3infra.io
      </a>
      <div className="hidden items-center gap-8 text-sm text-gray-400 md:flex">
        <a href="#capabilities" className="transition-colors hover:text-white">
          Capabilities
        </a>
        <a href="#architecture" className="transition-colors hover:text-white">
          Architecture
        </a>
        <a href="#delivery" className="transition-colors hover:text-white">
          Solutions
        </a>
      </div>
      <a
        href="mailto:web3infra.io@gmail.com"
        className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-bold text-black"
      >
        Contact <EnvelopeIcon className="h-4 w-4" />
      </a>
    </div>
  </nav>
);

const HeroSection = () => (
  <header
    id="top"
    className="relative min-h-[calc(100svh-112px)] overflow-hidden border-b border-white/10"
  >
    <TechnologyCanvas />
    <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
    <div className="container-v3 relative z-10 flex min-h-[calc(100svh-112px)] flex-col justify-between py-8 md:py-16">
      <div className="max-w-4xl pt-4 md:pt-16">
        <div className="mb-4 font-mono text-xs font-bold uppercase text-cyan-300 md:mb-6">
          Infrastructure for the onchain world
        </div>
        <h1 className="text-5xl font-black leading-[1.05] md:text-7xl">
          Web3 Engineering
          <span className="mt-3 block text-[0.58em] text-gray-400 md:text-[0.68em]">
            From infrastructure to product experience
          </span>
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-8 text-gray-300 md:mt-7 md:text-xl md:leading-9">
          Four foundational layers: contracts, wallets, algorithms, and
          gateways. Built for secure, clear, and continuously evolving onchain
          products.
        </p>
        <a
          href="#capabilities"
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3.5 font-bold text-black md:mt-8"
        >
          Explore capabilities <ArrowRightIcon className="h-4 w-4" />
        </a>
      </div>
      <dl className="mt-8 grid grid-cols-2 border-l border-t border-white/15 md:mt-12 lg:grid-cols-4">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className="border-b border-r border-white/15 bg-black/30 p-4 backdrop-blur-sm md:p-5"
          >
            <dt className="text-xs text-gray-500">{metric.label}</dt>
            <dd className="mt-2 font-mono text-lg font-bold text-white md:text-xl">
              {metric.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  </header>
);

const CapabilitySection = () => (
  <section
    id="capabilities"
    className="border-b border-white/10 pb-16 pt-8 md:py-28"
  >
    <div className="container-v3">
      <SectionHeading
        eyebrow="Core capabilities / 01"
        title="Four core layers. One complete system."
        description="Each layer can ship independently or combine into an end-to-end Web3 product architecture."
      />
      <div className="mt-14 grid gap-5 lg:grid-cols-2">
        {capabilities.map((capability) => (
          <article
            key={capability.title}
            className={`rounded-lg border border-white/10 border-t-2 bg-[#080808] p-7 md:p-9 ${
              capability.accent.split(" ")[1]
            }`}
          >
            <div className="flex items-start justify-between">
              <capability.icon
                className={`h-9 w-9 ${capability.accent.split(" ")[0]}`}
              />
              <span className="font-mono text-xs text-gray-600">
                {capability.index}
              </span>
            </div>
            <h3 className="mt-10 text-2xl font-black md:text-3xl">
              {capability.title}
            </h3>
            <p className="mt-2 font-mono text-xs uppercase text-gray-500">
              {capability.subtitle}
            </p>
            <p className="mt-5 max-w-xl leading-7 text-gray-300">
              {capability.summary}
            </p>
            <ul className="mt-7 grid gap-3 border-t border-white/10 pt-6 sm:grid-cols-2">
              {capability.items.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-2 font-mono text-xs text-gray-400"
                >
                  <CheckCircleIcon className="h-4 w-4 shrink-0 text-gray-600" />{" "}
                  {item}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const ArchitectureSection = () => (
  <section
    id="architecture"
    className="border-b border-white/10 bg-[#050505] py-20 md:py-28"
  >
    <div className="container-v3">
      <SectionHeading
        eyebrow="System architecture / 02"
        title="Connect every layer. Control every boundary."
        description="From user action to onchain finality, data, signatures, and state move through a clear engineering path."
      />
      <div className="mt-14 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
        {architecture.map((layer, index) => (
          <div
            key={layer.name}
            className="relative rounded-lg border border-white/10 bg-black p-5"
          >
            <div className="font-mono text-xs text-cyan-300">0{index + 1}</div>
            <div className="mt-8 text-lg font-bold">{layer.name}</div>
            <div className="mt-2 font-mono text-xs text-gray-500">
              {layer.detail}
            </div>
            {index < architecture.length - 1 && (
              <BoltIcon className="absolute -right-2 top-1/2 z-10 hidden h-4 w-4 -translate-y-1/2 bg-[#050505] text-lime-400 xl:block" />
            )}
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs text-gray-500">
        <span className="inline-flex items-center gap-2">
          <KeyIcon className="h-4 w-4" /> Signing boundary
        </span>
        <span className="inline-flex items-center gap-2">
          <ShieldCheckIcon className="h-4 w-4" /> Security boundary
        </span>
        <span className="inline-flex items-center gap-2">
          <CodeBracketIcon className="h-4 w-4" /> Integration boundary
        </span>
      </div>
    </div>
  </section>
);

const DeliverySection = () => (
  <section id="delivery" className="border-b border-white/10 py-20 md:py-28">
    <div className="container-v3">
      <SectionHeading
        eyebrow="What we build / 03"
        title="Web3 products we can deliver"
        description="Technology is not a showcase. It must become a product customers understand, teams operate, and users choose."
      />
      <div className="mt-14 grid border-l border-t border-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {useCases.map((useCase) => (
          <article
            key={useCase.title}
            className="min-h-52 border-b border-r border-white/10 p-7 transition-colors hover:bg-white/[0.03]"
          >
            <useCase.icon className="h-7 w-7 text-gray-400" />
            <h3 className="mt-10 text-xl font-bold">{useCase.title}</h3>
            <p className="mt-3 leading-7 text-gray-500">{useCase.detail}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const EngineeringSection = () => (
  <section className="border-b border-white/10 bg-[#050505] py-20 md:py-28">
    <div className="container-v3 grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
      <div>
        <SectionHeading
          eyebrow="Engineering / 04"
          title="Engineering quality sets the technical ceiling."
          description="Security, testing, deployment, and observability enter the delivery process at design time."
        />
        <ul className="mt-10 grid gap-px overflow-hidden rounded-lg border border-white/10 bg-white/10 sm:grid-cols-2">
          {engineeringControls.map((item) => (
            <li
              key={item}
              className="flex min-h-20 items-center gap-3 bg-[#050505] p-5 text-sm text-gray-300"
            >
              <ShieldCheckIcon className="h-5 w-5 shrink-0 text-emerald-400" />{" "}
              {item}
            </li>
          ))}
        </ul>
      </div>
      <aside className="rounded-lg border border-amber-400/50 bg-amber-300/[0.04] p-7 md:p-9">
        <div className="font-mono text-xs uppercase text-amber-300">
          Production boundary
        </div>
        <h2 className="mt-5 text-2xl font-black">Production Readiness</h2>
        <p className="mt-5 leading-8 text-gray-300">
          Before real assets go live, delivery should include independent
          security review, access governance, load testing, key management,
          incident planning, and continuous monitoring.
        </p>
        <div className="mt-8 border-t border-amber-300/20 pt-6 text-sm leading-7 text-gray-500">
          Technical strength is not only building features. It is knowing
          exactly where the system boundaries are.
        </div>
      </aside>
    </div>
  </section>
);

const StackSection = () => (
  <section className="py-16 md:py-20">
    <div className="container-v3 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <div className="font-mono text-xs uppercase text-gray-500">
          Technology stack / 05
        </div>
        <h2 className="mt-3 text-3xl font-black">
          Reliable tools. Deliberate composition.
        </h2>
      </div>
      <div className="flex max-w-4xl flex-wrap gap-2">
        {stack.map((item) => (
          <span
            key={item}
            className="rounded-lg border border-white/10 px-3 py-2 font-mono text-xs text-gray-400"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  </section>
);

const SectionHeading = ({ eyebrow, title, description }) => (
  <div className="max-w-3xl">
    <div className="font-mono text-xs font-bold uppercase text-cyan-300">
      {eyebrow}
    </div>
    <h2 className="mt-4 text-4xl font-black leading-tight md:text-5xl">
      {title}
    </h2>
    <p className="mt-5 max-w-2xl text-base leading-8 text-gray-400 md:text-lg">
      {description}
    </p>
  </div>
);

const CapabilitiesV3 = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const previousLanguage = document.documentElement.lang;
    document.title = "Web3infra.io | Web3 Engineering";
    document.documentElement.lang = "en";
    return () => {
      document.title = previousTitle;
      document.documentElement.lang = previousLanguage;
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <PageHeader />
      <main>
        <HeroSection />
        <CapabilitySection />
        <ArchitectureSection />
        <DeliverySection />
        <EngineeringSection />
        <StackSection />
      </main>
      <Web3Contact />
    </div>
  );
};

export default CapabilitiesV3;
